import { useSelector } from 'react-redux';
import { selectors } from 'darwin_core';
import { MODES } from 'darwin_core/utils/consts.js';
import {
  makeDhwModeNameHumanized,
  makeModeNameHumanized,
} from 'darwin_core/utils/helpers.js';

import ActionPanel from '../components/ActionPanel.jsx';
import {
  FireIcon,
  PlusHoursIcon,
  ScheduleIcon,
  SnowflakeIcon,
} from '../components/icons.jsx';
import { zoneActionPath } from '../navigation/paths.js';
import { plusHoursEndText } from '../controls/plusHoursEnd.js';

// The zone-screen action list — the web equivalent of the app's action-panel
// column (HeatingModeActionPanel · ScheduleActionPanel · PlusHoursActionPanel).
// Each panel navigates to its own dedicated screen (where the command logic
// lives) and carries a live status sub-line + icon, matching the app rather than
// the bare text links of the first cut.
//
// Panels grey out under the app's rules (see the gating block below). That is a
// cue, exactly as in the app — a browser user can still reach each screen by
// URL, so every destination keeps its own guard as well.

// The modes a Plus Hours / Boost hold may start from — the app's
// PlusHoursActionPanel `availableModes`. Everything else (Manual and Off on
// heating, On and Off on hot water) greys the panel out.
const PLUS_HOURS_MODES = [
  MODES.Auto,
  MODES.OverrideNext,
  MODES.OverridePlusHours,
];

export default function ZoneActions({ homeId, zoneId }) {
  const isDhw = useSelector((s) => selectors.isDhw(s, homeId, zoneId));
  const mode = useSelector((s) => selectors.getMode(s, homeId, zoneId));
  const inHoliday = useSelector((s) =>
    Boolean(selectors.isInHolidayMode(s, homeId, zoneId)),
  );

  // Heating Status — the humanized current mode. Pure (no schedule access): a
  // heating zone reads "Off · Frost protection" when off or on holiday (the app's
  // modeSubtext), a hot-water zone uses the DHW wording (Off/On/Auto/Boost).
  const off = mode === MODES.Off;
  const frost = off || inHoliday;
  let statusSub;
  if (isDhw) {
    statusSub = makeDhwModeNameHumanized(mode);
  } else {
    const base = makeModeNameHumanized(mode);
    statusSub = frost ? `${base} · Frost protection` : base;
  }
  const StatusIcon = frost ? SnowflakeIcon : FireIcon;

  // Schedule — the app's ScheduleActionPanel sub-line, through the very selector
  // the app reads (getNextEventText): "<target> at <time>" for the next event in
  // the zone's clock format, "Inactive" when Off or in holiday, nothing in Manual /
  // On — and, because it folds in a running Plus Hours / DSR period, the time that
  // override hands back to the schedule while one is active. Reading the raw
  // schedule instead (as this once did) disagreed with the app for exactly as long
  // as an override ran. The schedule SCREEN keeps its raw-schedule Now / Next
  // badges; only this summary follows the app. Guarded: the selector weekifies the
  // reported schedule with no shape checks, so a malformed frame must not take the
  // zone screen down.
  const scheduleSub = useSelector((s) => {
    try {
      return selectors.getNextEventText(s, homeId, zoneId);
    } catch {
      return 'View schedule';
    }
  });
  const rawSchedule = useSelector((s) => {
    const home = s.homeList.homes.find((h) => h.id === homeId);
    const zone = home?.zones?.find(
      (z) => z.zoneId?.join('_') === zoneId.join('_'),
    );
    return zone?.schedule;
  });
  const hasSchedule = Array.isArray(rawSchedule) && rawSchedule.length > 0;

  // Panel gating — the app's rules:
  //   • Schedule: a heating zone follows darwin_core's isScheduleButtonDisabled
  //     (live only in Auto with a schedule reported — so an override, Manual and
  //     Off all grey it out); a cylinder's panel is disabled outside Auto
  //     (HaloHotWaterZone). Guarded because getSchedule can throw for a zone that
  //     has never reported one — read that as "no schedule".
  //   • Plus Hours / Boost: disabled outside PLUS_HOURS_MODES and, as both zone
  //     screens pass disabled={!hasSchedule}, when there is no schedule to fall
  //     back to.
  //   • Heating Status is never gated by mode.
  const scheduleButtonDisabled = useSelector((s) => {
    try {
      return selectors.isScheduleButtonDisabled(s, homeId, zoneId);
    } catch {
      return true;
    }
  });
  const scheduleDisabled = isDhw ? mode !== MODES.Auto : scheduleButtonDisabled;
  const boostDisabled = !hasSchedule || !PLUS_HOURS_MODES.includes(mode);

  // Plus Hours / Boost — the app's PlusHoursActionPanel: "Until <time>" while a
  // hold is running (the clock time it hands back to the schedule, not a
  // countdown), else "Not selected". Scoped to an actual OverridePlus so a
  // scheduled override or DSR period does not read as a hold.
  const holdEnd = useSelector((s) => plusHoursEndText(s, homeId, zoneId));
  const boostSub =
    mode === MODES.OverridePlusHours && holdEnd
      ? `Until ${holdEnd}`
      : 'Not selected';

  return (
    <nav className="action-panel-list" aria-label="Zone actions">
      <ActionPanel
        to={zoneActionPath(homeId, zoneId, 'status')}
        icon={<StatusIcon />}
        title="Heating Status"
        sub={statusSub}
      />
      <ActionPanel
        to={zoneActionPath(homeId, zoneId, 'schedule')}
        icon={<ScheduleIcon />}
        title="Your Schedule"
        sub={scheduleSub}
        disabled={scheduleDisabled}
      />
      <ActionPanel
        to={zoneActionPath(homeId, zoneId, 'boost')}
        icon={<PlusHoursIcon />}
        title={isDhw ? 'Boost' : 'Plus Hours'}
        sub={boostSub}
        disabled={boostDisabled}
      />
    </nav>
  );
}
